import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * Button. House rules:
 *   - One primary per screen. Use `default` sparingly.
 *   - 200ms ease-fluid transition. Press-down on :active (translateY 1px).
 *   - Honour prefers-reduced-motion via motion-reduce: utilities.
 *   - Red is reserved for destructive intent. Never decorative.
 */
const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md',
    'text-sm font-medium tracking-tight ring-offset-background',
    'transition-all duration-fluid ease-fluid',
    'active:translate-y-px motion-reduce:transition-none motion-reduce:active:translate-y-0',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        default:     'bg-brand text-brand-foreground shadow-soft hover:opacity-90 active:opacity-80',
        secondary:   'bg-navy-100 text-navy-800 hover:bg-navy-200/80',
        outline:     'border border-slate-200 bg-white text-navy-900 hover:bg-navy-50 hover:border-navy-200',
        ghost:       'text-navy-800 hover:bg-navy-50',
        destructive: 'bg-negative text-white hover:bg-negative/90',
        link:        'text-brand underline-offset-4 hover:underline px-0 h-auto',
      },
      size: {
        default: 'h-9 px-3.5',
        sm:      'h-8 px-3 text-xs',
        lg:      'h-10 px-5',
        icon:    'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  ),
)
Button.displayName = 'Button'

export { Button, buttonVariants }
